import React, { useState } from 'react';
import { logUserActivityQuietly } from './supabaseClient';

function VancoCalculator({user}) {
    // 0. New Sub-Method State
    const [vancoMethod, setVancoMethod] = useState('kinetic'); // 'kinetic' or 'esrf'
    // 1. Input States
    const [dose, setDose] = useState('0');
    const [cPost, setCPost] = useState('0'); // Measured High
    const [cPre, setCPre] = useState('0');   // Measured Low
    const [tPrime, setTPrime] = useState('1'); // Time from end of infusion to Cpost
    const [tDoseToPre, setTDoseToPre] = useState('0.5'); // Time from Cpre to next dose
    const [weight, setWeight] = useState('0');
    const [infusionTime, setInfusionTime] = useState('1'); // T
    const [tau, setTau] = useState('0'); // Dosing Interval
    const [newDose, setNewDose] = useState('0');
    const [newTau, setNewTau] = useState('0'); // New Dosing Interval
    const [aucDesired, setAucDesired] = useState(450);
    const [age, setAge] = useState('0');
    const [gender, setGender] = useState(1.23); // Default to Male (1.23)
    const [scr, setScr] = useState(100);
    const [scrUnit, setScrUnit] = useState('umol'); // 'umol' or 'mgdl'

    const [esrfAge, setesrfAge] = useState('0');
    const [esrfWeight, setesrfWeight] = useState('0');
    const [esrfDose, setEsrfDose] = useState(0);
    const [esrfGender, setEsrfGender] = useState('');
    const [esrfSCr, setEsrfSCr] = useState ('');
    const [dtLevelDrawn, setDtLevelDrawn] = useState(""); // YYYY-MM-DDTHH:mm
    const [dtDialysisStarts, setDtDialysisStarts] = useState("");
    const [esrfCurrentTrough, setEsrfCurrentTrough] = useState ('');
    const [extractionRatio, setExtractionRatio] = useState(30); // Default to 30%
    const [simDose, setSimDose] = useState('');
    const [dtLastDoseGiven, setDtLastDoseGiven] = useState('');
    

    // 1. First, normalize the Creatinine
    const normalizedScr = scrUnit === 'mgdl' ? scr * 88.4 : scr;

    // 2. Second, calculate the CrCl (Now the computer knows what 'crcl' is)
    const crcl = ((140 - age) * weight * gender) / normalizedScr;

    // 3. Third, calculate your Ke options
   // Check if inputs make clinical sense
    const isValidInput = cPost > cPre && (tau - infusionTime - tPrime - tDoseToPre) > 0;

    // Only calculate if valid, otherwise default to 0 to prevent NaN
    const keVal = isValidInput 
        ? (Math.log(cPost / cPre) / (tau - infusionTime - tPrime - tDoseToPre)) 
        : 0;                    
    const keEstimated = 0.0044 + (crcl * 0.00083); // This will work now!

    // 4. Everything else follows
    const thalfVal = keVal > 0 ? 0.693 / keVal: 0;

    // Extrapolating to True Cmax and Cmin
    const cMaxTrue = cPost * Math.exp(keVal * tPrime);
    const cMinTrue = cPre * Math.exp(-keVal * tDoseToPre);

    // NEW Vd Calculation based on Hospital Practice
    // We use the extrapolated/true Peak and the measured Trough
    const vdVal = (isValidInput && (cMaxTrue - cPre) !== 0) ? (dose / (cMaxTrue - cMinTrue)) : 0;

    // For the L/kg display
    const vdLkg = (vdVal > 0 && weight > 0) ? (vdVal / weight) : 0;

    // Logic for Step 6 (Using the NEW/EXPECTED dose values)
    // Formula: New Dose / [Vd(L) * (1 - e^(-Ke * T))]
    // 1. Calculate the Denominator part (the accumulation factor)
    const accumulationFactor = 1 - Math.exp(-keVal * newTau);

    // 2. Safe Expected Cmax
    // We only calculate if we have a valid Vd, a valid Ke, and the accumulation factor is not zero
    const expCmax = (vdVal > 0 && accumulationFactor > 0) 
        ? (newDose / (vdVal * accumulationFactor)) 
        : 0;

    // 3. Safe Expected Cmin
    // We only calculate if expCmax is valid
    const expCmin = (expCmax > 0) 
        ? (expCmax * Math.exp(-keVal * newTau)) 
        : 0;
    // Part A: Calculate C0 (Extrapolated start concentration)
    // t_infusion is the time it took to give the dose (e.g., 1 or 2 hours)
    const c0 = cMaxTrue * Math.exp(keVal * infusionTime);
    // Part B: Calculate AUC for one dosing interval
    const aucSingle = (keVal > 0 && cMaxTrue >0) ? (c0 - cMinTrue) / keVal :0;
    // Part C: Calculate AUC 24
    const dosingFrequency = tau >0 ? 24 / tau : 0; // How many times a day? e.g., 2 for Q12H
    const auc24Log = (dosingFrequency > 0) ? aucSingle * dosingFrequency : 0;
    const aucInf = infusionTime * ((cMinTrue + cMaxTrue) / 2);
    const aucElim = (keVal > 0) ? (cMaxTrue - cMinTrue) / keVal : 0;
    // Total AUC for ONE dose
    const aucDose = aucInf + aucElim;
    const aucDaily = (tau > 0) ? (aucDose * (24 / tau)) : 0;

    // For New Dose AUC prediction
    // Step 5: Safe Recommended Total Daily Dose (TDD)
    const currentTDD = (tau > 0) ? (dose * (24 / tau)) : 0;
    const recommendedTDD = (aucDaily > 0 && currentTDD > 0) 
        ? (aucDesired / aucDaily) * currentTDD 
        : 0;

    //ESRF MATH LOGIC
    // Step 1: Calculate ESRF CrCl
    const esrfCrClValue = esrfAge > 0 && esrfWeight > 0 && esrfSCr > 0 
        ? ((140 - esrfAge) * esrfWeight) / (72 * (esrfSCr / 88.4)) * (esrfGender === 'female' ? 0.85 : 1.0)
        : 0;
    // Step 2: Hospital ESRF Ke Formula
    const esrfKe = 0.0044 + (0.00083 * esrfCrClValue);
    // Step 2: ESRF Vd calculation
    const getEsrfVd = () => {
        // Formula: 0.9 L/kg * weight
        return 0.9 * esrfWeight;
    };

    // Step 3: Half-life (Optional but good for transparency)
    const getEsrfHalfLife = () => {
        const ke = esrfKe;
        return ke > 0 ? 0.693 / ke : 0;
    };
    const calculateExactHours = () => {
        if (!dtLevelDrawn || !dtDialysisStarts) return 0;
        
        const start = new Date(dtLevelDrawn);
        const end = new Date(dtDialysisStarts);
        
        // Difference in milliseconds converted to hours
        const diffInHours = (end - start) / (1000 * 60 * 60);
        
        return diffInHours > 0 ? diffInHours : 0;
    };

    const hoursGap = calculateExactHours();
    const truePreHDTrough = esrfCurrentTrough * Math.exp(-esrfKe * hoursGap);
    // Step 4: Account for 30% reduction during HD
    const estimatedPostHDLevel = truePreHDTrough * (1 - (extractionRatio / 100));
    // Step 5: Calculate Predicted Cmax after new dose
    const predictedCmax = estimatedPostHDLevel> 0? estimatedPostHDLevel + (esrfDose / getEsrfVd()):0;
    const calculateLongInterval = () => {
        if (!dtLastDoseGiven || !dtDialysisStarts) return 0;
        
        const start = new Date(dtLastDoseGiven);
        const end = new Date(dtDialysisStarts);
        
        const diffInHours = (end - start) / (1000 * 60 * 60);
        return diffInHours > 0 ? diffInHours : 0;
    };

    const longInterval = calculateLongInterval();
  

    return (
      <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
        <button 
          type="button"
          className="no-print" 
          onClick={() => {
                          const actualUserEmail = user && user.email ? user.email : 'anonymous-user';
                          const trackingLabel = `${actualUserEmail}-printed-Vanco`;
                          logUserActivityQuietly(trackingLabel); 
                          window.print();
                      }}
          style={{
            padding: '10px 20px',
            backgroundColor: '#27ae60', 
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.2)'
          }}
        >
          🖨️ Print PDF Report
        </button>
      </div>
        <div style={{background: '#111416', minHeight: '100vh', color: '#fff', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', padding: '20px', boxSizing: 'border-box' }}>
          <h1 style={{ textAlign: 'center', color: '#3498db' }}>Vanco TDM Worksheet</h1>
          
          <details style={{
            backgroundColor: '#1E293B',
            border: '1px solid #38BDF8',
            borderRadius: '8px',
            padding: '10px 15px',
            marginBottom: '20px',
            cursor: 'pointer'
          }}>
            <summary style={{ color: '#38BDF8', fontWeight: 'bold' }}>
            ⏱️ Check Clinical Sampling Protocol (Click to expand)
            </summary>
            <div style={{ marginTop: '12px', padding: '15px', background: 'rgba(0,0,0,0.15)', borderRadius: '8px', border: '1px solid #3d4e5d', fontSize: '0.9rem', color: '#F8FAFC' }}>
    
                {/* SECTION 1: SAMPLING TIMELINE TABLE */}
                <div style={{ marginBottom: '15px' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#00d2ff', textTransform: 'uppercase', display: 'block', marginBottom: '6px', letterSpacing: '0.5px' }}>
                        ⏱️ Clinical Sampling Schedule
                    </span>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem' }}>
                        <tbody>
                            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                <td style={{ padding: '6px 0', fontWeight: 'bold', color: '#fff', width: '40%' }}>Normal Renal Function:</td>
                                <td style={{ padding: '6px 0', color: '#bdc3c7' }}>Draw steady-state levels **after the 3rd dose**.</td>
                            </tr>
                            <tr>
                                <td style={{ padding: '6px 0', fontWeight: 'bold', color: '#fff', verticalAlign: 'top' }}>Impaired Renal Function:</td>
                                <td style={{ padding: '6px 0', color: '#bdc3c7', lineHeight: '1.4' }}>
                                    Draw **after 24 hours** or **after the 1st dose** to prevent early accumulation.
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* SECTION 2: METRIC TARGET BADGES */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '15px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                    {/* Trough Card */}
                    <div style={{ background: 'rgba(52, 152, 219, 0.1)', border: '1px solid rgba(52, 152, 219, 0.2)', padding: '10px', borderRadius: '6px' }}>
                        <div style={{ fontWeight: 'bold', color: '#3498db', fontSize: '0.8rem', textTransform: 'uppercase' }}>📉 Trough Target (CMin):</div>
                        <div style={{ fontSize: '0.85rem', marginTop: '4px', color: '#ecf0f1' }}>
                            Draw **30 mins prior** or immediately before the next scheduled dose.
                        </div>
                    </div>

                    {/* Peak Card */}
                    <div style={{ background: 'rgba(155, 89, 182, 0.1)', border: '1px solid rgba(155, 89, 182, 0.2)', padding: '10px', borderRadius: '6px' }}>
                        <div style={{ fontWeight: 'bold', color: '#9b59b6', fontSize: '0.8rem', textTransform: 'uppercase' }}>📈 Peak Target (CMax):</div>
                        <div style={{ fontSize: '0.85rem', marginTop: '4px', color: '#ecf0f1' }}>
                            Draw exactly **1 Hour** following the complete end of the IV infusion.
                        </div>
                    </div>
                </div>

                {/* SECTION 3: OTHER LEVEL MODALITIES */}
                <div style={{ background: 'rgba(255,255,255,0.03)', padding: '10px', borderRadius: '6px', fontSize: '0.85rem', lineHeight: '1.4', marginBottom: '12px' }}>
                    📌 <strong style={{ color: '#fff' }}>Stat Dose / Random Levels:</strong> Timing relies strictly on the current serum concentration clinical picture or target trough monitoring rules.
                </div>

                {/* SECTION 4: THERAPEUTIC INDEX PROFILE */}
                <div style={{ background: 'rgba(46, 204, 113, 0.08)', borderLeft: '4px solid #2ecc71', padding: '10px', borderRadius: '0 6px 6px 0' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#2ecc71', textTransform: 'uppercase', display: 'block' }}>
                        🎯 Target Reference Window
                    </span>
                    <div style={{ fontSize: '0.9rem', color: '#fff', marginTop: '4px' }}>
                        <strong>15 - 20 mcg/L</strong> for complicated infections. Target <strong style={{ color: '#f1c40f' }}>$AUC_{24}$ of 400 - 600</strong>.
                    </div>
                </div>

            </div>
          </details>
          {/* SUB-MENU TOGGLE */}
          <div className="no-print" style={{ textAlign: 'center', marginBottom: '20px' }}>
            <button onClick={() => setVancoMethod('kinetic')} style={subBtnStyle(vancoMethod === 'kinetic')}>
              Peak/Trough (Kinetic)
            </button>
            <button onClick={() => setVancoMethod('esrf')} style={subBtnStyle(vancoMethod === 'esrf')}>
              Trough Only (ESRF/Dialysis)
            </button>
          </div>
          <hr style={{ border: '0.5px solid #eee', marginBottom: '30px' }} />
          
          {/* CONDITIONAL RENDERING FOR VANCO METHODS */}
          {vancoMethod === 'kinetic' ? (
            <>
              {/* INPUT SECTION */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px', background: '#1E293B', padding: '20px', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                <div>
                  <label>Age (years):</label><br/>
                  <input type="number" value={age} onChange={(e) => setAge(e.target.value)} style={{width: '80%'}} />
                </div>
                <div>
                  <label>Serum Creatinine:</label><br/>
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <input type="number" value={scr} onChange={(e) => setScr(Number(e.target.value))} style={{ width: '60%' }} />
                    <select value={scrUnit} onChange={(e) => setScrUnit(e.target.value)}style={{ width: '35%' }}>
                      <option value="umol">µmol/L</option>
                      <option value="mgdl">mg/dL</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label>Gender:</label><br/>
                  <select value={gender} onChange={(e) => setGender(parseFloat(e.target.value))} style={{width: '85%', padding: '5px'}}>
                    <option value={1.23}>Male (1.23)</option>
                    <option value={1.04}>Female (1.04)</option>
                  </select>
                </div>
                <div>
                  <label>Body Weight (kg):</label><br/>
                  <input type="number" value={weight} onChange={(e) => setWeight(e.target.value)} style={{width: '80%'}} />
                </div>
              </div>
              {/* LIVE FORMULA SECTION */}
              <div style={{ background: '#1E293B', color: '#dfe6e9', padding: '25px', borderRadius: '10px' }}>
                        {/* Step 0: Creatinine Clearance */}
                <h3 style={{ color: '#3498db', marginTop: 0, Fontfamily: "'Inter' sans-serif" }}>Step 0: Estimated CrCl</h3>
                <div style={{ fontSize: '1.1rem', backgroundColor: '#3b4446', padding: '15px', borderRadius: '5px', marginBottom: '30px' }}>
                  {scrUnit === 'mgdl' && (
                    <p style={{ fontSize: '0.9rem', color: '#bdc3c7' }}>
                      * Converting {scr} mg/dL to {normalizedScr.toFixed(1)} µmol/L
                    </p>
                  )}
                  Calculation: <br/>
                  [(140 - <span style={{color:'orange'}}>{age}</span>) × <span style={{color:'orange'}}>{weight}</span> × <span style={{color:'orange'}}>{gender}</span>] / <span style={{color:'orange'}}>{normalizedScr.toFixed(1)}</span> = 
                  <strong style={{color: '#3498db'}}> {crcl.toFixed(1)} ml/min</strong>
                </div>
                
                <h3 style={{ color: '#00cec9', marginTop: '30px', Fontfamily: "'Inter' sans-serif" }}>Step 1: Elimination Rate (ke)</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px'}}>
                  <div>
                    <label>Lab Measured C-post:</label><br/>
                    <input type="number" value={cPost} onChange={(e) => setCPost(e.target.value)} style={{width: '80%'}} />
                  </div>
                  <div>
                    <label>Lab Measured C-pre:</label><br/>
                    <input type="number" value={cPre} onChange={(e) => setCPre(e.target.value)} style={{width: '80%'}} />
                  </div>
                  <div>
                    <label>Dose (mg):</label><br/>
                    <input type="number" value={dose} onChange={(e) => setDose(e.target.value)} style={{width: '80%'}} />
                  </div>
                  <div>
                    <label>Dosing Interval (Δt):</label><br/>
                    <input type="number" value={tau} onChange={(e) => setTau(e.target.value)} style={{width: '80%'}} />
                  </div>
                  <div>
                    <label>Infusion Time T (hr):</label><br/>
                    <input type="number" value={infusionTime} onChange={(e) => setInfusionTime(e.target.value)} style={{width: '80%'}} />
                  </div>
                  <div>
                    <label>Time End Infusion to C-post (t'):</label><br/>
                    <input type="number" value={tPrime} onChange={(e) => setTPrime(e.target.value)} style={{width: '80%'}} />
                  </div>
                  <div>
                    <label>Time C-pre to next dose:</label><br/>
                    <input type="number" value={tDoseToPre} onChange={(e) => setTDoseToPre(e.target.value)} style={{width: '80%'}} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '20px' }}>
      
                  {/* Left Side: Measured Formula */}
                  <div style={{ flex: 1, padding: '15px', border: '1px solid #f39c12', borderRadius: '8px', background: '#2d3436' }}>
                    <p style={{ color: '#f39c12', fontWeight: 'bold', marginTop: '0px' }}>Option A: Measured (Patient Levels)</p>
                    <p style={{ fontSize: '0.9rem', color: '#bdc3c7' }}>Formula: ln(Cpost / Cpre) / Δt_elim</p>
                    <p style={{ fontSize: '1rem' }}>
                      ln({cPost} / {cPre}) / ({tau} - {infusionTime} - {tPrime} - {tDoseToPre})
                    </p>
                    <p style={{ fontSize: '1.2rem', marginTop: '10px' }}>
                      Result: <strong style={{ color: '#f39c12' }}>{keVal.toFixed(4)} hr⁻¹</strong>
                    </p>
                  </div>

                  {/* Right Side: Estimated Formula */}
                  <div style={{ flex: 1, padding: '15px', border: '1px solid #3498db', borderRadius: '8px', background: '#2d3436' }}>
                    <p style={{ color: '#3498db', fontWeight: 'bold', marginTop: 0 }}>Option B: Estimated (Population/CrCl)</p>
                    <p style={{ fontSize: '0.9rem', color: '#bdc3c7' }}>Formula: 0.0044 + (CrCl × 0.00083)</p>
                    <p style={{ fontSize: '1rem' }}> 0.0044 + ({crcl.toFixed(2)} × 0.00083)</p>
                    <p style={{ fontSize: '1.2rem', marginTop: '10px' }}>Result: <strong style={{ color: '#3498db' }}>{keEstimated.toFixed(4)} hr⁻¹</strong></p>
                  </div>

                </div>
                <p style={{ fontSize: '0.8rem', marginTop: '10px', fontStyle: 'italic', color: '#95a5a6' }}>
                  * Validation: If Measured Ke is significantly higher than Estimated Ke, consider Augmented Renal Clearance or sampling error.
                </p>
                
                <p>Calculation: t½ = 0.693 / <span style={{color:'cyan'}}>{keVal.toFixed(4)}</span> = <strong>{thalfVal.toFixed(2)} hours</strong></p>
                <h3 style={{ color: '#00cec9', marginTop: '30px' }}>Step 2: True Cmax and Cmin</h3>
                <p>Cmax = <span style={{color:'orange'}}>{cPost}</span> × e^(<span style={{color:'cyan'}}>{keVal.toFixed(4)}</span> × <span style={{color:'orange'}}>{tPrime}</span>) = <strong style={{color:'#55efc4'}}>{cMaxTrue.toFixed(2)} mcg/mL</strong></p>
                <p>Cmin = <span style={{color:'orange'}}>{cPre}</span> × e^-(<span style={{color:'cyan'}}>{keVal.toFixed(4)}</span> × <span style={{color:'orange'}}>{tDoseToPre}</span>) = <strong style={{color:'#55efc4'}}>{cMinTrue.toFixed(2)} mcg/mL</strong></p>

                <h3 style={{ color: '#00cec9', marginTop: '30px' }}>Step 3: Volume of Distribution (Vd)</h3>
                <p>
                  Vd = <span style={{ color: 'orange' }}>{dose}</span> / 
                  [<span style={{ color: '#55efc4' }}>{cMaxTrue.toFixed(2)}</span> - 
                  <span style={{ color: '#55efc4' }}>{cMinTrue.toFixed(2)}</span>]
                </p>
                <p style={{ fontSize: '1.2rem', backgroundColor: '#3b4446', padding: '10px', borderRadius: '5px' }}>
                  Vd = <strong>{vdVal.toFixed(2)} L</strong> 
                  <span style={{ fontSize: '0.9rem', marginLeft: '10px', color: '#bdc3c7' }}>
                    ({vdLkg.toFixed(2)} L/kg)
                  </span>
                </p>
              </div>
      
              {/* Step 5: AUC 24 Calculation */}
              <div style={{ background: '#1E293B', color: '#dfe6e9', padding: '25px', borderRadius: '10px',marginTop: '30px'}}>
                <h3 style={{ color: '#a29bfe', marginTop: 0 }}>Step 4: AUC 24 (Log Method)</h3>
                    <div style={{ background: '#3b4446', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
                      <p style={{ margin: '0 0 5px 0', fontSize: '0.85rem', color: '#bdc3c7' }}>
                          Formula: AUC = (C₀ - Cmin) / kₑ
                      </p>
                      <div style={{ fontSize: '1rem', fontFamily: 'monospace' }}>
                          C₀ = {cMaxTrue.toFixed(2)} × e^({keVal.toFixed(3)} × {infusionTime}) = 
                          <strong style={{ color: '#55efc4' }}> {c0.toFixed(2)} mg/L</strong>
                      </div>
                      <div>
                          AUC = ({c0.toFixed(2)} - {cMinTrue.toFixed(2)}) / {keVal.toFixed(4)} = 
                          <strong style={{ color: '#55efc4' }}> {aucSingle.toFixed(2)} mg·h/L</strong>
                      </div>
                      <div>
                          auc24Log = {aucSingle.toFixed(2)} * {dosingFrequency} = 
                          <strong style={{ color: '#55efc4' }}> {auc24Log.toFixed(1)}mg·h/L </strong>
                      </div>
                    </div>
                    <div style={{ textAlign: 'center', padding: '15px', border: '1px solid #a29bfe', borderRadius: '8px' }}>
                      <span style={{ fontSize: '0.9rem', color: '#bdc3c7' }}>Predicted AUC 24:</span>
                      <div style={{ fontSize: '2.2rem', fontWeight: 'bold', color: '#55efc4' }}>
                          {auc24Log.toFixed(1)} 
                      </div>
                      <p style={{ margin: 0, fontSize: '0.75rem', color: (auc24Log >= 400 && auc24Log <= 600) ? '#55efc4' : '#ff7675' }}>
                          Target: 400 - 600 mg·h/L
                      </p>
                    </div>
              </div>
              <div style={{ background: '#1E293B', color: '#dfe6e9', padding: '25px', borderRadius: '10px', marginTop: '30px', borderLeft: '10px solid #a29bfe' }}>
                <h3 style={{ color: '#a29bfe', marginTop: 0 }}>Step 5: AUC 24 (Linear-Log Trapezoidal)</h3>

                {/* Part A: Infusion Area */}
                <div style={{ marginBottom: '20px' }}>
                  <p style={{ color: '#a29bfe', fontWeight: 'bold' }}>A. Area during Infusion (Trapezoidal)</p>
                  <p style={{ fontSize: '0.9rem' }}>Formula: T × [(cMinTrue.toFixed(2) + cMaxTrue.toFixed(2)) / 2]</p>
                  <p style={{ backgroundColor: '#3b4446', padding: '10px', borderRadius: '5px' }}>
                    {infusionTime} × [(<span style={{color:'#55efc4'}}>{cMinTrue.toFixed(2)}</span> + <span style={{color:'#55efc4'}}>{cMaxTrue.toFixed(2)}</span>) / 2] = <strong>{aucInf.toFixed(2)}</strong>
                  </p>
                </div>

                {/* Part B: Elimination Area */}
                <div style={{ marginBottom: '20px' }}>
                  <p style={{ color: '#a29bfe', fontWeight: 'bold' }}>B. Area during Elimination (Log-Linear)</p>
                  <p style={{ fontSize: '0.9rem' }}>Formula: (Cmax - Cmin) / ke</p>
                  <p style={{ backgroundColor: '#3b4446', padding: '10px', borderRadius: '5px' }}>
                    (<span style={{color:'#55efc4'}}>{cMaxTrue.toFixed(2)}</span> - <span style={{color:'#55efc4'}}>{cMinTrue.toFixed(2)}</span>) / <span style={{color:'cyan'}}>{keVal.toFixed(4)}</span> = <strong>{aucElim.toFixed(2)}</strong>
                  </p>
                </div>

                {/* Part C: Total AUC 24 */}
                <div style={{ borderTop: '1px solid #636e72', paddingTop: '15px' }}>
                  <p style={{ color: '#a29bfe', fontWeight: 'bold' }}>C. Total Daily AUC (AUC24)</p>
                  <p style={{ fontSize: '0.9rem' }}>Formula: (AUC_inf + AUC_elim) × (24 / τ)</p>
                  <p style={{ fontSize: '1.4rem', textAlign: 'center', backgroundColor: '#3b4446', padding: '15px', borderRadius: '5px', border: '2px dashed #a29bfe' }}>
                    ({aucInf.toFixed(2)} + {aucElim.toFixed(2)}) × (24 / <span style={{color:'orange'}}>{tau}</span>) = 
                    <strong style={{ color: aucDaily > 600 ? '#ff7675' : aucDaily < 400 ? '#ffeaa7' : '#55efc4', marginLeft: '10px' }}>
                      {aucDaily.toFixed(2)} mg·h/L
                    </strong>
                  </p>
                  <p style={{ fontSize: '0.9rem', color: '#bdc3c7', textAlign: 'center', marginTop: '10px' }}>
                    Target Range: 400 - 600 mg·h/L | Current Goal: 
                    <input 
                      type="number" 
                      value={aucDesired} 
                      onChange={(e) => setAucDesired(Number(e.target.value))} 
                      style={{ 
                        width: '50px', 
                        background: '#f2f2f9', 
                        border: 'none', 
                        borderBottom: '1px solid #3498db', 
                        color: '#253cd0', 
                        fontWeight: 'bold', 
                        textAlign: 'center',
                        marginLeft: '5px'
                      }} 
                    />
                    <br />
                    {aucDaily > 600 ? " ⚠️ High Risk (Nephrotoxicity)" : aucDaily < 400 ? " ⚠️ Sub-therapeutic" : " ✅ Within Target"}
                  </p>
                  <p style={{ fontSize: '0.9rem', color: '#bdc3c7', textAlign: 'center', marginTop: '10px' }}>
                    Target: 400 - 600 mg·h/L | 
                    {aucDaily > 600 ? " ⚠️ High Risk (Nephrotoxicity)" : aucDaily < 400 ? " ⚠️ Sub-therapeutic" : " ✅ Within Target"}
                  </p>
                </div>
                {/* Transparency Logic for Dose Adjustment */}
                <div style={{ 
                  margin: '15px auto', 
                  padding: '10px', 
                  background: '#edf4f81a', 
                  borderLeft: '4px solid #3498db',
                  fontSize: '0.85rem',
                  maxWidth: '90%'
                }}>
                  <p style={{ margin: '0 0 5px 0', color: '#bdc3c7', fontWeight: 'bold' }}>
                    Adjustment Strategy: Linear Proportionality
                  </p>
                  <div style={{ fontFamily: 'monospace', color: '#ecf0f1' }}>
                    (Target AUC {aucDesired} / Current AUC {aucDaily.toFixed(2)}) × Current TDD ({ (currentTDD).toFixed(0) } mg)
                    <br />
                    = {aucDaily >0 ?  (aucDesired / aucDaily).toFixed(4):'--' } factor × { (currentTDD).toFixed(0) } mg
                    <br />
                    = <strong>{recommendedTDD.toFixed(0)} mg/day</strong>
                  </div>
                </div>

                {/* Recommended Dose based on AUC */}
                <div style={{ marginTop: '20px', padding: '10px', backgroundColor: 'rgba(85, 239, 196, 0.1)', border: '1px solid #55efc4', borderRadius: '5px' }}>
                  <p style={{ margin: 0, fontSize: '1rem', color: '#55efc4' }}>
                    <strong>Dose Adjustment:</strong> To achieve Target AUC 450, the required Total Daily Dose (TDD) is 
                    <strong> {recommendedTDD.toFixed(0)} mg/day</strong>.
                  </p>
                </div>
                <div style={{color: '#7f8c8d', textAlign: 'center'}}>
                  [Current Peak/Trough Worksheet Content]
                </div>
              </div>
              <div style={{ 
                background: '#3c4e52', 
                padding: '15px', 
                borderRadius: '8px 8px 0 0', 
                border: '1px solid #3498db', 
                borderBottom: 'none',
                marginTop: '30px',
                display: 'flex',
                gap: '20px'
              }}>
                <div style={{ flex: 1 }}>
                  <label style={{ color: '#2980b9', fontWeight: 'bold', fontSize: '0.9rem' }}>New Planned Dose (mg):</label><br/>
                  <input type="number" value={newDose} onChange={(e) => setNewDose(e.target.value)} style={{ width: '90%', padding: '8px', marginTop: '5px', borderRadius: '4px', border: '1px solid #bdc3c7' }}/>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ color: '#2980b9', fontWeight: 'bold', fontSize: '0.9rem' }}>New Interval Tau (hr):</label><br/>
                  <input type="number" value={newTau} onChange={(e) => setNewTau(e.target.value)} style={{ width: '90%', padding: '8px', marginTop: '5px', borderRadius: '4px', border: '1px solid #bdc3c7' }}/>
                </div>
              </div>
              <div style={{ background: '#1E293B', color: '#ecf0f1', padding: '25px', borderRadius: '10px', marginTop: '30px', borderLeft: '10px solid #3498db' }}>
                <h3 style={{ color: '#3498db', marginTop: 0 }}>Step 6: Expected Outcomes (New Dose)</h3>
                {/* Logic for Expected Cmax */}
                <p style={{ fontSize: '1.1rem' }}>
                    Formula Expected Cmax: <span style={{ color: '#bdc3c7' }}>{newDose} / [{vdVal.toFixed(2)} × (1 - e^(-{keVal.toFixed(4)} × {newTau}))]</span>
                </p>
                <p style={{ fontSize: '1.3rem', backgroundColor: '#34495e', padding: '15px', borderRadius: '5px' }}>
                    Expected Cmax: <strong style={{ color: '#3498db' }}>{expCmax.toFixed(2)} mcg/mL</strong>
                </p>
                {/* Logic for Expected Cmin */}
                <p style={{ fontSize: '1.1rem', marginTop: '20px' }}>
                    Formula Expected Cmin: <span style={{ color: '#bdc3c7' }}>{expCmax.toFixed(2)} × e^(-{keVal.toFixed(4)} × {newTau})</span>
                </p>
                <p style={{ fontSize: '1.3rem', backgroundColor: '#34495e', padding: '15px', borderRadius: '5px' }}>
                    Expected Cmin: <strong style={{ color: '#3498db' }}>{expCmin.toFixed(2)} mcg/mL</strong>
                </p>
                {/* HANDBOOK DISCLAIMER LEGEND FOOTER FOR MEDICAL FILING */}
                <p style={{ margin: '12px 0 0 0', fontSize: '0.62rem', color: '#7f8c8d', fontStyle: 'italic', textAlign: 'center', lineHeight: '1.3' }}>
                  *Evaluation derived from official Clinical Pharmacokinetics Handbook criteria. Patient-specific clinical conditions and absolute biomarker trends must always override automated population estimates prior to final intervention execution.
                </p>
              </div>
            </>
          ) : (
            <>
              {/* THIS IS THE NEW ESRF SECTION */}
              {/* ESRF INPUTS (Only what is needed for ESRF) */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', background: '#3c4e52', padding: '20px', borderRadius: '8px', border: '1px solid #e67e22', marginBottom: '20px' }}>
                <div>
                  <label>Age (years):</label><br/>
                  <input type="number" value={esrfAge} onChange={(e) => setesrfAge(e.target.value)} style={{width: '80%'}} />
                </div>
                <div>
                  <label>Body Weight (kg):</label><br/>
                  <input type="number" value={esrfWeight} onChange={(e) => setesrfWeight(e.target.value)} style={{width: '80%'}} />
                </div>
                <div>
                  <label>Lab Current Trough (mcg/mL):</label><br/>
                  <input type="number" value={esrfCurrentTrough} onChange={(e) => setEsrfCurrentTrough(e.target.value)} style={{width: '80%'}} />
                </div>
                <div>
                  <label>ESRF Dose (mg):</label><br/>
                  <input type="number" value={esrfDose} onChange={(e) => setEsrfDose(e.target.value)} style={{width: '80%'}} />
                </div>
              </div>
              <div style={{ background: '#1a252f', padding: '15px', borderRadius: '8px', border: '1px solid #34495e' }}>
                  <h4 style={{ color: '#ecf0f1', marginTop: 0 }}>Step 1: Residual Renal Function</h4>
                  {/* Inputs for ESRF Renal Panel */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      <div>
                          <label>SCr (µmol/L):</label>
                          <input 
                              type="number" 
                              value={esrfSCr} 
                              onChange={(e) => setEsrfSCr(Number(e.target.value))} 
                              style={inputStyle}
                          />
                      </div>
                      <div>
                          <label>Gender:</label>
                          <select onChange={(e) => setEsrfGender(e.target.value)} style={inputStyle}>
                              <option value="male">Male</option>
                              <option value="female">Female</option>
                          </select>
                      </div>
                  </div>
                  {/* Result Display */}
                  <div style={{ marginTop: '15px', color: '#f1c40f', fontFamily: 'monospace' }}>
                      Calculated CrCl: <strong>{esrfCrClValue.toFixed(1)} mL/min</strong> <br />
                      {/* Safety Guardrail for ESRF Calculator */}
                      {esrfCrClValue > 30 && (
                          <div style={{ 
                              backgroundColor: '#e74c3c', 
                              color: 'white', 
                              padding: '15px', 
                              borderRadius: '8px', 
                              marginBottom: '20px',
                              border: '2px solid #c0392b',
                              fontWeight: 'bold',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px'
                          }}>
                              <span>⚠️</span>
                              <div>
                                  Warning: Calculated CrCl is {esrfCrClValue.toFixed(1)} mL/min. 
                                  This patient may not be in ESRF. Please use the Standard Vancomycin Calculator.
                              </div>
                          </div>
                      )}
                      Kₑ = 0.0044 + (0.00083 × {esrfCrClValue.toFixed(1)}) = 
                      <strong style={{ fontSize: '1.1rem' }}> {esrfKe.toFixed(4)} hr⁻¹</strong>
                  </div>
              </div>
              {/* Transparency Summary Block */}
              <div style={{ marginTop: '20px', padding: '15px', background: '#2c3e50', borderRadius: '8px', border: '1px solid #34495e' }}>
                <h4 style={{ color: '#ecf0f1', margin: '0 0 10px 0', borderBottom: '1px solid #34495e', paddingBottom: '5px' }}>
                    Step 2: ESRF Pharmacokinetic Summary
                </h4>
                {/* Display Half-life Transparency */}
                <div style={{ marginBottom: '10px' }}>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#bdc3c7' }}>Elimination Half-life (t½):</p>
                    <code style={{ color: '#e74c3c', fontSize: '1rem' }}>
                        0.693 / {esrfKe.toFixed(4)} = <strong>{getEsrfHalfLife().toFixed(1)} Hours</strong>
                    </code>
                </div>
                {/* Display Vd Transparency */}
                <div style={{ marginBottom: '10px' }}>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#bdc3c7' }}>Volume of Distribution (Vᵈ):</p>
                    <code style={{ color: '#3498db', fontSize: '1rem' }}>
                        0.9 L/kg × {esrfWeight} kg = <strong>{getEsrfVd().toFixed(1)} Liters</strong>
                    </code>
                </div>
                {/* Summary Note */}
                <p style={{ margin: '10px 0 0 0', fontSize: '0.8rem', fontStyle: 'italic', color: '#95a5a6' }}>
                    *Note: Calculations based on hospital ESRF regression model (Kₑ = 0.0044 + 0.00083×CrCl).
                </p>
              </div>
              <div style={{ background: '#34495e', padding: '15px', borderRadius: '8px', marginTop: '10px' }}>
                  <h4 style={{ color: '#ecf0f1', margin: '0 0 10px 0' }}>Step 3:📅 Date/Time Lag Adjustment</h4>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      <div>
                          <label style={{ fontSize: '0.8rem', color: '#bdc3c7' }}>Date/Time Blood Drawn:</label>
                          <input 
                              type="datetime-local" 
                              value={dtLevelDrawn} 
                              onChange={(e) => setDtLevelDrawn(e.target.value)} 
                              style={inputStyle} 
                          />
                      </div>
                      <div>
                          <label style={{ fontSize: '0.8rem', color: '#bdc3c7' }}>Date/Time Dialysis Starts:</label>
                          <input 
                              type="datetime-local" 
                              value={dtDialysisStarts} 
                              onChange={(e) => setDtDialysisStarts(e.target.value)} 
                              style={inputStyle} 
                          />
                      </div>
                  </div>

                  <div style={{ marginTop: '15px', borderTop: '1px dashed #7f8c8d', paddingTop: '10px' }}>
                      <p style={{ margin: 0, fontSize: '0.9rem', color: '#f1c40f' }}>
                          Total Lag Time: <strong>{hoursGap.toFixed(1)} hours</strong>
                      </p>
                      <code style={{ fontSize: '1rem' }}>
                          <div>
                            CurrentTrough × e^(-esrfKe × hoursGap) = Ctrue Min
                          </div>
                          {esrfCurrentTrough} × e^(-{esrfKe.toFixed(4)} × {hoursGap.toFixed(1)}) = 
                          <strong style={{ color: '#2ecc71' }}> {truePreHDTrough.toFixed(1)} mcg/mL</strong>
                      </code>
                      <p style={{ color: '#2ecc71', fontWeight: 'bold', marginTop: '5px',fontSize: '1.2rem' }}>
                          Actual Level at Start of HD: {truePreHDTrough.toFixed(1)} mcg/mL
                      </p>
                  </div>
              </div>
              <div style={{ marginTop: '20px', padding: '15px', background: '#1e272e', borderRadius: '8px', border: '1px solid #ff3f34' }}>
                  <h4 style={{ color: '#ff5e57', margin: '0 0 10px 0' }}>Step 4:💡 Dialysis Washout Effect</h4>
                  
                  <div style={{ fontFamily: 'monospace' }}>
                      <p style={{ margin: 0, color: '#bdc3c7' }}>Pre-HD Level: {truePreHDTrough.toFixed(1)} mcg/mL</p>
                      
                      {/* Clickable/Editable Percentage Line */}
                      <p style={{ margin: '5px 0', color: '#bdc3c7', display: 'flex', alignItems: 'center' }}>
                          Dialysis Removal: -
                          <input 
                              type="number" 
                              value={extractionRatio} 
                              onChange={(e) =>{const val = e.target.value; setExtractionRatio(val === "" ? "" : Number(val))}}
                              style={{ 
                                  background: 'transparent', 
                                  border: 'none', 
                                  borderBottom: '1px dashed #bdc3c7', 
                                  color: '#ffdd59', 
                                  width: '45px', 
                                  fontSize: '1rem', 
                                  textAlign: 'center',
                                  fontWeight: 'bold',
                                  outline: 'none',
                                  margin: '0 2px'
                              }}
                          />
                          %
                      </p>
                      
                      <div style={{ fontSize: '1.2rem', color: '#ffdd59', marginTop: '10px' }}>
                          Estimated Post-HD Level: 
                          <strong> {estimatedPostHDLevel.toFixed(1)} mcg/mL</strong>
                      </div>
                  </div>
              </div>
              <div style={{ marginTop: '20px', padding: '15px', background: '#1e272e', borderRadius: '8px', border: '1px solid #ff3f34' }}>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#bdc3c7' }}>Step 5: Predicted Peak (Cₘₐₓ):</p>
                <code style={{ color: '#2e7acc', fontSize: '1.1rem' }}>
                    <div>
                    Ctrue Min + (Dose / EsrfVd) = Cmax
                    </div>
                    <div>
                    {estimatedPostHDLevel.toFixed(1)} + ({esrfDose} / {getEsrfVd().toFixed(1)}) = 
                    <strong> {predictedCmax.toFixed(1)} mcg/mL</strong>
                    </div>
                </code>
                
                {/* Clinical Target Check */}
                <p style={{ 
                    fontSize: '0.8rem', 
                    color: predictedCmax > 40 ? '#e74c3c' : '#95a5a6',
                    marginTop: '5px' 
                }}>
                    {predictedCmax > 40 ? "⚠️ Warning: Predicted Peak is high (> 40). Monitor for toxicity." : "✅ Predicted Peak is within safe range (25-40)."}
                </p>
            </div>
            <div style={{ background: '#09123e', padding: '15px', borderRadius: '8px', marginTop: '20px', color: 'white' }}>
                <h4 style={{ margin: '0 0 10px 0' }}>Step 6:🧪 Dose Simulation Testing</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div>
                      <label style={{ fontSize: '0.8rem' }}>Test Dose (mg):</label>
                      <input 
                          type="number" 
                          value={simDose} 
                          onChange={(e) => setSimDose(Number(e.target.value))} 
                          style={inputStyle} 
                      />
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                      <label style={{ fontSize: '0.8rem', color: '#bdc3c7' }}>Date/Time of LAST Dose Given:</label>
                      <input 
                          type="datetime-local" 
                          value={dtLastDoseGiven} 
                          onChange={(e) => setDtLastDoseGiven(e.target.value)} 
                          style={inputStyle} 
                      />
                      <p style={{ margin: '5px 0 0 0', fontSize: '0.85rem', color: '#f1c40f' }}>
                          Time elapsed since last dose: <strong>{longInterval.toFixed(1)} hours</strong>
                      </p>
                  </div>
                </div>
                {/* Peak Calculation Formula */}
                <div style={{ marginBottom: '15px' }}>
                    <p style={{ margin: '0 0 5px 0', fontSize: '0.85rem', color: '#bdc3c7' }}>1. Predicted Peak (Cₘₐₓ):</p>
                    <code style={{ display: 'block', background: 'rgba(0,0,0,0.3)', padding: '8px', borderRadius: '4px', marginBottom: '5px', fontSize: '0.9rem' }}>
                        Formula: Cₚₒₛₜ₋ₕ₀ + (Dose / Vᵈ) <br />
                        {estimatedPostHDLevel.toFixed(1)} + ({simDose} / {getEsrfVd().toFixed(1)}) = 
                        <strong style={{ color: '#2ecc71' }}> {estimatedPostHDLevel > 0 && simDose > 0 ?(estimatedPostHDLevel + (simDose / getEsrfVd())).toFixed(1):"0.0"} mcg/mL</strong>
                    </code>
                </div>

                {/* Trough Calculation Formula */}
                <div style={{ marginBottom: '10px' }}>
                    <p style={{ margin: '0 0 5px 0', fontSize: '0.85rem', color: '#bdc3c7' }}>2. Predicted Trough (Cₘᵢₙ) at Next HD:</p>
                    <code style={{ display: 'block', background: 'rgba(0,0,0,0.3)', padding: '8px', borderRadius: '4px', fontSize: '0.9rem' }}>
                        Formula: Cₘₐₓ × e⁻ᵏᵉᵗ <br />
                        {((estimatedPostHDLevel + (simDose / getEsrfVd()))).toFixed(1)} × e^(-{esrfKe.toFixed(4)} × {longInterval.toFixed(1)}) = 
                        <strong style={{ color: '#f1c40f' }}> {estimatedPostHDLevel > 0 && simDose >0 && longInterval >0 ? ((estimatedPostHDLevel + (simDose / getEsrfVd())) * Math.exp(-esrfKe * longInterval)).toFixed(1):"0.0"} mcg/mL</strong>
                    </code>
                </div>

                <p style={{ margin: '10px 0 0 0', fontSize: '0.75rem', color: '#7f8c8d', fontStyle: 'italic' }}>
                    *Where t = {longInterval.toFixed(1)} hours (Actual time elapsed from Last Dose to Next Dialysis).
                </p>
                {/* HANDBOOK DISCLAIMER LEGEND FOOTER FOR MEDICAL FILING */}
                <p style={{ margin: '12px 0 0 0', fontSize: '0.62rem', color: '#7f8c8d', fontStyle: 'italic', textAlign: 'center', lineHeight: '1.3' }}>
                  *Evaluation derived from official Clinical Pharmacokinetics Handbook criteria. Patient-specific clinical conditions and absolute biomarker trends must always override automated population estimates prior to final intervention execution.
                </p>
            </div>
            </>
          )}  
        </div>
      </>
    );
  }

  const inputStyle = {
      width: '80%',
      padding: '8px',
      marginTop: '5px',
      borderRadius: '4px',
      border: '1px solid #ccc',
      backgroundColor: '#fff',
      color: '#333'
  };
  // Style helper for the Sub-Buttons (Kinetic vs ESRF)
  const subBtnStyle = (active) => ({
    padding: '8px 15px',
    margin: '0 5px',
    backgroundColor: active ? '#e67e22' : '#ecf0f1',
    color: active ? 'white' : '#7f8c8d',
    border: active ? 'none' : '1px solid #bdc3c7',
    borderRadius: '20px', 
    cursor: 'pointer',
    fontSize: '0.8rem'
  });
  
export default VancoCalculator;